import React, { useEffect, useState } from 'react';
import ViewMessage from '@components/ViewMessage';
import { CircledExclamationPointIcon, EnvelopeIcon, LoaderIcon } from '@repo/icons';
import { useLocation, useSearchParams } from 'react-router-dom';
import { apiRequest } from '@repo/utils/helpers/api';
import styles from './VerifyEmail.module.scss';
import { setVerifyEmailRedirect } from '@repo/utils/helpers/verify-email-redirect';
import { supportHref } from '@repo/utils/helpers/preferred-email-client';
import {
  labelForSharedResourceType,
  SharedResourceType,
} from '@repo/redux/reducers/data/shareStatuses';
import { capitalize } from '@repo/utils/helpers/string';

export default function VerifyEmail({
  sharedResourceType,
}: {
  sharedResourceType: SharedResourceType;
}) {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [obscuredEmail, setObscuredEmail] = useState('');
  const [didError, setDidError] = useState(false);
  const [wasDisinvited, setWasDisinvited] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const resourceLabel = labelForSharedResourceType[sharedResourceType];

  async function fetchEmailVerificationData(signal: AbortSignal): Promise<void> {
    try {
      const verificationDataID = searchParams.get('id');
      const shareWaiting = await apiRequest<any>(
        'GET',
        `/share-waiting/${verificationDataID}/`,
        undefined,
        { apiVersion: 'v2' },
      );

      if (signal.aborted) {
        return;
      }

      if (shareWaiting.access_granted) {
        window.location.href = location.pathname;
      }

      setObscuredEmail(shareWaiting.email);
    } catch (e) {
      console.error(e);
      setWasDisinvited(true);
    }
  }

  const verify = async () => {
    try {
      const abortController = new AbortController();
      const verificationDataID = searchParams.get('id');
      setEmailSending(true);
      setVerifyEmailRedirect(location.pathname);
      await apiRequest<any>('PUT', `/share-waiting/${verificationDataID}/`, undefined, {
        apiVersion: 'v2',
      });

      if (abortController.signal.aborted) {
        return;
      }
      setEmailSent(true);
    } catch (e) {
      console.error(e);
      setDidError(true);
    }
  };

  const icon = () => {
    if (wasDisinvited || didError) {
      return <CircledExclamationPointIcon />;
    }

    return <EnvelopeIcon />;
  };

  const headline = () => {
    if (wasDisinvited) {
      return `No Access to ${capitalize(resourceLabel)}`;
    }

    if (didError) {
      return 'Sorry, something went wrong';
    }

    return 'Verify Your Email';
  };

  const message = () => {
    if (wasDisinvited) {
      return (
        <>
          It looks like you no longer
          <br />
          have access to this {resourceLabel}.
          <br />
          Contact the profile's owner.
        </>
      );
    }

    if (didError) {
      return (
        <>
          Something went wrong.
          <br />
          Please get in touch for assistance.
        </>
      );
    }

    if (emailSent) {
      return (
        <>
          We just sent an email to
          <br />
          {obscuredEmail}.
          <br />
          If you need assistance, please
        </>
      );
    }

    return (
      <>
        This {resourceLabel} was shared with
        <br />
        {obscuredEmail}. Please verify your
        <br />
        email address.
      </>
    );
  };

  const button = () => {
    if (wasDisinvited) {
      return undefined;
    }

    if (didError || emailSent) {
      return {
        text: 'Reach Out',
        onClick: () => window.open(supportHref()),
      };
    }

    if (emailSending) {
      return {
        text: (
          <>
            Sending Email <LoaderIcon />
          </>
        ),
        onClick: () => null,
        className: styles.loadingBtn,
      };
    }

    return {
      text: 'Verify Email',
      onClick: verify,
    };
  };

  useEffect(() => {
    const abortController = new AbortController();
    fetchEmailVerificationData(abortController.signal);
    return () => abortController.abort();
  }, []);

  return <ViewMessage icon={icon()} headline={headline()} message={message()} button={button()} />;
}
